import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';
import FeedbackBanner from '../../components/feature/FeedbackBanner';

export default function ArticlePlanejamentoTributario() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead 
        title="Planejamento Tributário: Estratégias para Reduzir Impostos Legalmente - Ativo News"
        description="Descubra as melhores estratégias de planejamento tributário para empresas brasileiras. Aprenda a reduzir a carga tributária de forma legal e eficiente."
        keywords="planejamento tributário, redução de impostos, elisão fiscal, gestão tributária"
        canonical="https://ativonews.com.br/artigo/planejamento-tributario"
      />
      <SchemaOrg type="Article" data={{
        headline: "Planejamento Tributário: Estratégias para Reduzir Impostos Legalmente",
        description: "Descubra as melhores estratégias de planejamento tributário para empresas brasileiras. Aprenda a reduzir a carga tributária de forma legal e eficiente.",
        author: "Dr. Fernando Almeida",
        datePublished: "2025-01-10",
        image: "https://public.readdy.ai/ai/img_res/183f712eae2fc69d13178c48591a0fb8.jpg"
      }} />
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <FeedbackBanner 
          articleTitle="Planejamento Tributário: Estratégias para Reduzir Impostos Legalmente"
          articleUrl={window.location.href}
        />

        {/* Hero do Artigo */}
        <article className="max-w-4xl mx-auto px-4 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-600 mb-8">
            <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/categoria/fiscal-tributario" className="hover:text-teal-600 transition-colors">
              Fiscal &amp; Tributário
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900">Planejamento Tributário</span>
          </nav>

          {/* Categoria */}
          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full">
              Fiscal &amp; Tributário
            </span>
          </div>

          {/* Título */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Planejamento Tributário: Como Reduzir Impostos Legalmente (Guia Completo)
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Descubra as estratégias lícitas para organizar operações, escolher o regime tributário ideal e reduzir a carga tributária da sua empresa dentro da lei. Guia completo com conceitos, métodos práticos, checklist e orientações sobre elisão fiscal.
          </p>

          {/* Meta informações */}
          <div className="flex items-center gap-6 pb-8 mb-8 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                <i className="ri-user-line text-2xl text-gray-400"></i>
              </div>
              <div>
                <div className="font-semibold text-gray-900">Dr. Ricardo Santos</div>
                <div className="text-sm text-gray-600">Especialista em Tributação</div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                10 de janeiro de 2025
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                18 min
              </span>
            </div>
          </div>

          {/* Imagem destacada */}
          <div className="w-full h-96 rounded-2xl overflow-hidden mb-12 bg-gray-100">
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20tax%20planning%20documents%20calculator%20financial%20charts%20modern%20office%20desk%20organized%20workspace%20business%20strategy%20graphs%20financial%20data%20analysis%20clean%20minimalist%20style%20natural%20lighting%20professional%20photography&width=1200&height=600&seq=planejamento-tributario-hero-guia&orientation=landscape" 
              alt="Planejamento Tributário"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* TL;DR - Resumo Executivo */}
          <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-10 rounded-r-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <i className="ri-flashlight-line text-green-600"></i>
              TL;DR — Resumo Executivo
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Planejamento tributário é o conjunto de decisões lícitas para organizar operações, contratos e regime de tributação visando <strong>reduzir, postergar ou evitar tributos dentro da lei</strong> (elisão fiscal). No Brasil, isso envolve escolher corretamente entre Simples Nacional, Lucro Presumido e Lucro Real, classificar atividades (CNAE/NCM), aproveitar créditos tributários, estruturar contratos e precificação, organizar folha e pró-labore, e buscar incentivos setoriais. A diferença entre elisão (legal) e evasão (ilegal) é clara: planejamento tributário é documentado, rastreável e coerente. Com a Reforma Tributária (CBS/IBS), o planejamento se torna ainda mais crítico. Este guia apresenta as 7 alavancas principais, checklist prático, orientações sobre regimes tributários e erros que geram autuação.
            </p>
          </div>

          {/* Sumário Interativo */}
          <div className="bg-gray-50 rounded-xl p-6 mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-list-check text-green-600"></i>
              Sumário
            </h2>
            <nav className="space-y-2">
              <a href="#o-que-e" className="block text-teal-600 hover:text-teal-700 transition-colors">
                → O que é Planejamento Tributário (e o que não é)
              </a>
              <a href="#elisao-evasao" className="block text-teal-600 hover:text-teal-700 transition-colors">
                → Elisão x Evasão: a linha que você não pode cruzar
              </a>
              <a href="#alavancas" className="block text-teal-600 hover:text-teal-700 transition-colors">
                → As 7 alavancas principais para reduzir impostos legalmente
              </a>
              <a href="#regimes" className="block text-teal-600 hover:text-teal-700 transition-colors">
                → Simples Nacional, Lucro Presumido e Lucro Real: como escolher
              </a>
              <a href="#checklist" className="block text-teal-600 hover:text-teal-700 transition-colors">
                → Planejamento tributário na rotina: checklist mensal
              </a>
              <a href="#reforma" className="block text-teal-600 hover:text-teal-700 transition-colors">
                → Reforma Tributária (IBS/CBS): por que isso muda seu planejamento
              </a>
              <a href="#erros" className="block text-teal-600 hover:text-teal-700 transition-colors">
                → Erros comuns que viram autuação (e como evitar)
              </a>
              <a href="#faq" className="block text-teal-600 hover:text-teal-700 transition-colors">
                → FAQ
              </a>
            </nav>
          </div>

          {/* Conteúdo do artigo */}
          <div className="prose prose-lg max-w-none">
            
            {/* Seção 1 */}
            <section id="o-que-e" className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">O que é Planejamento Tributário (e o que não é)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Planejamento tributário é <strong>planejar antes do fato gerador</strong>: estruturar a empresa e suas operações para pagar o mínimo possível sem violar a legislação. Isso inclui analisar regime tributário, natureza jurídica, enquadramento da atividade, local de prestação/venda, precificação, créditos tributários e organização documental.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                O conceito está diretamente associado à <strong>elisão fiscal</strong> — práticas lícitas que aproveitam lacunas, opções e alternativas previstas na legislação para reduzir a carga tributária. É um direito do contribuinte organizar seus negócios da forma mais eficiente possível.
              </p>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg mb-4">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-600"></i>
                  O que NÃO é planejamento tributário:
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Omitir receita ou faturamento</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Emitir nota fiscal "por fora" (caixa dois)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Simular operação sem substância econômica (empresa de fachada)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Manipular documentos fiscais para reduzir imposto</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Usar laranjas ou interpostas pessoas</span>
                  </li>
                </ul>
                <p className="text-gray-700 text-sm mt-3">
                  <strong>Importante:</strong> essas práticas configuram <strong>evasão fiscal</strong> (ilegal) e podem resultar em multas pesadas, processos criminais e fechamento da empresa.
                </p>
              </div>
            </section>

            {/* Seção 2 */}
            <section id="elisao-evasao" className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Elisão x Evasão: a linha que você não pode cruzar</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                    <h3 className="text-xl font-bold text-gray-900">Elisão Fiscal (Legal)</h3>
                  </div>
                  <p className="text-gray-700 text-sm mb-3">
                    Práticas lícitas, planejadas, para reduzir ou postergar tributos usando alternativas legais previstas na legislação.
                  </p>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                      <span>Escolher regime tributário mais vantajoso</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                      <span>Estruturar contratos e operações</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                      <span>Aproveitar créditos tributários</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                      <span>Buscar incentivos fiscais legais</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-close-circle-fill text-red-600 text-2xl"></i>
                    <h3 className="text-xl font-bold text-gray-900">Evasão Fiscal (Ilegal)</h3>
                  </div>
                  <p className="text-gray-700 text-sm mb-3">
                    Práticas ilícitas com objetivo de diminuir ou extinguir tributos através de sonegação, fraude ou omissões.
                  </p>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <i className="ri-close-line text-red-600 mt-0.5"></i>
                      <span>Omitir receitas e operações</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-line text-red-600 mt-0.5"></i>
                      <span>Falsificar documentos fiscais</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-line text-red-600 mt-0.5"></i>
                      <span>Criar operações fictícias</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-line text-red-600 mt-0.5"></i>
                      <span>Usar interpostas pessoas (laranjas)</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-blue-600"></i>
                  Regra prática para distinguir:
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">✓</span>
                    <span>Se você precisa <strong>esconder do Fisco</strong> para funcionar, é um mau sinal (provavelmente evasão)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">✓</span>
                    <span>Se é <strong>documentável, rastreável e coerente</strong> com a operação real, tende a ser planejamento lícito (elisão)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">✓</span>
                    <span>Se há <strong>substância econômica</strong> (operação real, não fictícia), está no caminho certo</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Seção 3 */}
            <section id="alavancas" className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">As 7 alavancas principais para reduzir impostos legalmente</h2>
              
              <div className="space-y-6">
                {/* Alavanca 1 */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Escolha correta do regime tributário</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        A decisão entre <strong>Simples Nacional, Lucro Presumido e Lucro Real</strong> costuma ser a maior fonte de economia — e também a mais negligenciada. A diferença pode representar <strong>10% a 20% da receita</strong> em alguns casos.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        O ideal é <strong>simular anualmente</strong> com base em:
                      </p>
                      <ul className="mt-2 space-y-1 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-green-600 mt-0.5"></i>
                          <span>Faturamento mensal projetado</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-green-600 mt-0.5"></i>
                          <span>Margem de lucro real</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-green-600 mt-0.5"></i>
                          <span>Folha de pagamento e pró-labore</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-green-600 mt-0.5"></i>
                          <span>Tipo de cliente (B2B x B2C)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-green-600 mt-0.5"></i>
                          <span>Despesas dedutíveis e créditos possíveis</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Alavanca 2 */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Classificação fiscal correta (CNAE, NCM e código de serviço)</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Erros de enquadramento geram consequências graves:
                      </p>
                      <ul className="space-y-1 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                          <span><strong>Alíquota errada:</strong> você pode pagar mais do que deveria</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                          <span><strong>Obrigação acessória incorreta:</strong> declarações e apurações erradas</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                          <span><strong>Risco de autuação:</strong> fiscalização pode questionar e autuar</span>
                        </li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Dica prática:</strong> revise a classificação sempre que adicionar novos produtos/serviços ou mudar atividades.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Alavanca 3 */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Revisão de créditos tributários (quando aplicável)</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Em regimes não cumulativos (como Lucro Real em algumas situações), <strong>créditos podem reduzir o custo efetivo</strong> de PIS/COFINS e outros tributos.
                      </p>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Exemplos de créditos possíveis:
                      </p>
                      <ul className="space-y-1 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-purple-600 mt-0.5"></i>
                          <span>Aquisição de insumos e matéria-prima</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-purple-600 mt-0.5"></i>
                          <span>Energia elétrica e combustíveis</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-purple-600 mt-0.5"></i>
                          <span>Aluguéis e serviços de terceiros (em alguns casos)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-purple-600 mt-0.5"></i>
                          <span>Frete e logística</span>
                        </li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Atenção:</strong> créditos exigem documentação fiscal correta e podem ser questionados. Mantenha controles rigorosos.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Alavanca 4 */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Estruturação contratual e precificação</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Como você estrutura contratos pode mudar significativamente a incidência de tributos:
                      </p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-orange-600 mt-0.5"></i>
                          <span><strong>Venda + serviço separados:</strong> pode ter tratamentos tributários diferentes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-orange-600 mt-0.5"></i>
                          <span><strong>Assinatura vs. venda avulsa:</strong> pode alterar reconhecimento de receita e tributos</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-orange-600 mt-0.5"></i>
                          <span><strong>Reembolso de despesas:</strong> se bem estruturado, pode não compor base de cálculo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-orange-600 mt-0.5"></i>
                          <span><strong>Locação vs. venda:</strong> tributação completamente diferente</span>
                        </li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Importante:</strong> sempre respeite a <strong>substância econômica</strong>. Contratos devem refletir a operação real.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Alavanca 5 */}
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      5
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Organização de folha, pró-labore e distribuição de lucros</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Tema recorrente em elisão fiscal: a remuneração de sócios pode ser estruturada com equilíbrio entre pró-labore e distribuição de lucros — sempre com lastro contábil e escrituração correta.
                      </p>
                      <div className="bg-white border border-teal-200 rounded-lg p-4 mb-3">
                        <p className="text-gray-700 text-sm mb-2"><strong>Pró-labore:</strong></p>
                        <ul className="space-y-1 text-gray-700 text-sm ml-4">
                          <li>• Incide INSS (11% a 20%, dependendo do valor)</li>
                          <li>• Incide IRRF (tabela progressiva até 27,5%)</li>
                          <li>• É despesa dedutível para a empresa (em alguns regimes)</li>
                        </ul>
                      </div>
                      <div className="bg-white border border-teal-200 rounded-lg p-4">
                        <p className="text-gray-700 text-sm mb-2"><strong>Distribuição de lucros:</strong></p>
                        <ul className="space-y-1 text-gray-700 text-sm ml-4">
                          <li>• Isenta de IR (dentro de limites e com lucro contábil real)</li>
                          <li>• Não incide INSS</li>
                          <li>• Deve respeitar o lucro apurado e escrituração regular</li>
                        </ul>
                      </div>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Atenção crítica:</strong> distribuição de lucros sem lastro contábil ou desproporcional pode ser questionada como distribuição disfarçada de pró-labore. Mantenha contabilidade rigorosa.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Alavanca 6 */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      6
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Benefícios e incentivos fiscais (setoriais e regionais)</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Dependendo do setor e localidade, podem existir incentivos significativos:
                      </p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-map-pin-line text-indigo-600 mt-0.5"></i>
                          <span><strong>Zonas Francas e áreas de incentivo:</strong> Manaus, Nordeste, etc.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-book-line text-indigo-600 mt-0.5"></i>
                          <span><strong>Lei do Bem (inovação tecnológica):</strong> dedução extra de P&amp;D</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-recycle-line text-indigo-600 mt-0.5"></i>
                          <span><strong>PADIS/PATVD:</strong> semicondutores e displays</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-building-line text-indigo-600 mt-0.5"></i>
                          <span><strong>ISS reduzido:</strong> alguns municípios oferecem alíquotas menores para determinadas atividades</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-plant-line text-indigo-600 mt-0.5"></i>
                          <span><strong>Exportação:</strong> imunidade/isenção de diversos tributos</span>
                        </li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Aqui o contador tributarista agrega muito valor:</strong> pesquisa, documentação, governança e acompanhamento de obrigações acessórias específicas.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Alavanca 7 */}
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      7
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Documentação e "prova" do planejamento</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        <strong>Planejamento tributário bom é aquele que resiste a uma fiscalização.</strong> Para isso, é essencial:
                      </p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-file-text-line text-red-600 mt-0.5"></i>
                          <span><strong>Premissas documentadas:</strong> por que escolheu determinado regime ou estrutura</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-calculator-line text-red-600 mt-0.5"></i>
                          <span><strong>Memória de cálculo:</strong> simulações que justificam a decisão</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-user-line text-red-600 mt-0.5"></i>
                          <span><strong>Trilha de aprovação:</strong> quem decidiu, quando e com base em quê</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-shield-check-line text-red-600 mt-0.5"></i>
                          <span><strong>Evidências de substância:</strong> contratos, pagamentos, operações reais</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-time-line text-red-600 mt-0.5"></i>
                          <span><strong>Revisão periódica:</strong> o planejamento deve ser atualizado quando o cenário muda</span>
                        </li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Lembre-se:</strong> sem documentação, mesmo uma tese correta vira risco operacional. O Fisco pode questionar e você precisa provar a licitude.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Seção 4 - Regimes Tributários */}
            <section id="regimes" className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Simples Nacional, Lucro Presumido e Lucro Real: como escolher</h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                A escolha do regime tributário é a decisão mais impactante no planejamento tributário. Cada regime tem regras, alíquotas e obrigações diferentes. A melhor escolha depende de múltiplos fatores.
              </p>

              {/* Simples Nacional */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-store-2-line text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Simples Nacional</h3>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  Regime simplificado para micro e pequenas empresas com <strong>faturamento até R$ 4,8 milhões/ano</strong>. Unifica até 8 tributos em uma única guia (DAS).
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-thumb-up-line text-green-600"></i>
                      Vantagens:
                    </h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Simplificação (uma guia para vários tributos)</li>
                      <li>• Alíquotas geralmente menores (depende do anexo)</li>
                      <li>• Menos obrigações acessórias</li>
                      <li>• Redução de carga administrativa</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-thumb-down-line text-red-600"></i>
                      Desvantagens:
                    </h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Limite de receita (R$ 4,8 mi/ano)</li>
                      <li>• Não permite aproveitar créditos tributários</li>
                      <li>• Pode ser mais caro dependendo da atividade/anexo</li>
                      <li>• Restrições por tipo de atividade</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white border border-green-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">Quando o Simples é indicado:</h4>
                  <ul className="space-y-1 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                      <span>Faturamento até R$ 4,8 milhões/ano</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                      <span>Atividade com anexo vantajoso (especialmente comércio e indústria em anexos I, II e III)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                      <span>Folha de pagamento significativa (melhora Fator R para serviços)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                      <span>Empresa valoriza simplicidade operacional</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Lucro Presumido */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-briefcase-line text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Lucro Presumido</h3>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  Regime para empresas com <strong>faturamento até R$ 78 milhões/ano</strong>. O lucro tributável é calculado por presunção (percentual sobre a receita), não pelo lucro real.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-thumb-up-line text-blue-600"></i>
                      Vantagens:
                    </h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Previsibilidade (tributos calculados por presunção)</li>
                      <li>• Vantajoso quando margem real é alta</li>
                      <li>• Menos complexidade que Lucro Real</li>
                      <li>• Bom para serviços com margem elevada</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-thumb-down-line text-red-600"></i>
                      Desvantagens:
                    </h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Não aproveita prejuízo fiscal</li>
                      <li>• Pode ser caro se margem for baixa</li>
                      <li>• PIS/COFINS cumulativo (alíquota total ~3,65%)</li>
                      <li>• Menos flexibilidade para planejamento</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white border border-blue-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">Quando o Presumido é indicado:</h4>
                  <ul className="space-y-1 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                      <span>Faturamento entre R$ 4,8 mi e R$ 78 mi/ano (aproximadamente)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                      <span>Margem de lucro real acima da presunção (ex: serviços de consultoria, advocacia, TI)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                      <span>Poucas despesas dedutíveis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                      <span>Operação estável e previsível</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Lucro Real */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-4-line text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Lucro Real</h3>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  Regime obrigatório para empresas com <strong>faturamento acima de R$ 78 milhões/ano</strong> e opcional para as demais. Tributos calculados sobre o lucro contábil ajustado.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-thumb-up-line text-purple-600"></i>
                      Vantagens:
                    </h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Tributos sobre lucro real (pode ser vantajoso)</li>
                      <li>• Aproveita prejuízos fiscais</li>
                      <li>• PIS/COFINS não cumulativo (pode ter créditos)</li>
                      <li>• Mais flexibilidade para planejamento tributário</li>
                      <li>• Deduções de despesas operacionais</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-thumb-down-line text-red-600"></i>
                      Desvantagens:
                    </h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Alta complexidade contábil e fiscal</li>
                      <li>• Muitas obrigações acessórias (ECF, ECD, EFD)</li>
                      <li>• Custo de compliance elevado</li>
                      <li>• Exige contabilidade rigorosa</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white border border-purple-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">Quando o Lucro Real é indicado:</h4>
                  <ul className="space-y-1 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-0.5"></i>
                      <span>Faturamento acima de R$ 78 milhões/ano (obrigatório)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-0.5"></i>
                      <span>Margem de lucro baixa ou prejuízo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-0.5"></i>
                      <span>Muitas despesas dedutíveis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-0.5"></i>
                      <span>Possibilidade de aproveitar créditos de PIS/COFINS</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-0.5"></i>
                      <span>Atividades específicas (bancos, factoring, etc.)</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Regra de ouro */}
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-trophy-line text-amber-600 text-xl"></i>
                  Regra de ouro:
                </h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Não escolha regime "por costume" ou porque "todo mundo usa".</strong>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Faça <strong>simulação anual detalhada</strong> considerando projeções realistas de faturamento, despesas, margem e tipo de operação. Reavalie periodicamente, especialmente quando houver mudanças significativas no negócio.
                </p>
              </div>
            </section>

            {/* Seção 5 - Checklist Mensal */}
            <section id="checklist" className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Planejamento tributário na rotina: checklist mensal</h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                Planejamento tributário não é algo que se faz "uma vez por ano". É um processo contínuo que exige revisão mensal de indicadores, documentos e conformidade.
              </p>

              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-calendar-check-line text-teal-600"></i>
                  Checklist Mensal de Planejamento Tributário
                </h3>
                
                <div className="space-y-3">
                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">1. Conferir faturamento, notas emitidas e cancelamentos</h4>
                        <p className="text-gray-700 text-sm">Garanta que todas as operações estão corretamente registradas e que não há erros de emissão.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">2. Verificar classificação fiscal (CNAE, NCM, código de serviço)</h4>
                        <p className="text-gray-700 text-sm">Revise se novos produtos/serviços estão corretamente classificados e se não há erros recorrentes.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">3. Mapear despesas com potencial de crédito ou dedução</h4>
                        <p className="text-gray-700 text-sm">Identifique despesas que podem gerar créditos tributários ou serem dedutíveis (quando aplicável ao regime).</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">4. Revisar folha de pagamento e pró-labore</h4>
                        <p className="text-gray-700 text-sm">Verifique se a estrutura de remuneração continua adequada e se há oportunidades de otimização (respeitando limites legais).</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">5. Monitorar obrigações acessórias e prazos</h4>
                        <p className="text-gray-700 text-sm">Acompanhe entregas de declarações, guias e documentos fiscais para evitar multas e juros.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">6. Acompanhar indicadores-chave (KPIs tributários)</h4>
                        <p className="text-gray-700 text-sm">
                          • Carga tributária efetiva (% sobre receita)<br />
                          • Tributos pagos vs. orçado<br />
                          • Prazo médio de recolhimento<br />
                          • Contingências fiscais
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">7. Revisar contratos e precificação</h4>
                        <p className="text-gray-700 text-sm">Analise se novos contratos ou mudanças de preço impactam a tributação e ajuste estratégias quando necessário.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">8. Documentar decisões e premissas</h4>
                        <p className="text-gray-700 text-sm">Mantenha registros de todas as escolhas tributárias e justificativas (essencial para auditorias e fiscalizações).</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">9. Acompanhar mudanças na legislação</h4>
                        <p className="text-gray-700 text-sm">Fique atento a novas leis, portarias e instruções normativas que possam impactar seu planejamento.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-teal-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex-shrink-0 mt-0.5"></div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">10. Reunião mensal de revisão (fiscal + financeiro + gestão)</h4>
                        <p className="text-gray-700 text-sm">Alinhe áreas sobre performance tributária, riscos identificados e oportunidades de melhoria.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Seção 6 - Reforma Tributária */}
            <section id="reforma" className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Reforma Tributária (IBS/CBS): por que isso muda seu planejamento</h2>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                A <strong>Reforma Tributária</strong> aprovada em 2023 e regulamentada pela Lei Complementar 214/2025 muda radicalmente o sistema tributário brasileiro. A partir de 2026, começam os testes de CBS (Contribuição sobre Bens e Serviços) e IBS (Imposto sobre Bens e Serviços), que substituirão gradualmente PIS/COFINS, ICMS e ISS.
              </p>

              <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-alert-line text-orange-600"></i>
                  Impactos no Planejamento Tributário:
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-white border border-orange-200 rounded-lg p-4">
                    <h4 className="font-bold text-gray-900 mb-2">1. Mudança de regime de cumulativo para não-cumulativo</h4>
                    <p className="text-gray-700 text-sm">
                      CBS e IBS funcionam como IVA (Imposto sobre Valor Agregado), com <strong>não-cumulatividade plena</strong>. Isso significa que créditos tributários se tornam muito mais relevantes — empresas que compram insumos, materiais e serviços poderão abater os tributos pagos na cadeia.
                    </p>
                  </div>

                  <div className="bg-white border border-orange-200 rounded-lg p-4">
                    <h4 className="font-bold text-gray-900 mb-2">2. Revisão completa de precificação</h4>
                    <p className="text-gray-700 text-sm">
                      Com mudança de alíquotas e sistemática de cálculo, <strong>preços finais ao consumidor</strong> precisarão ser revisados. A transparência tributária também aumenta: tributos destacados separadamente nas notas fiscais.
                    </p>
                  </div>

                  <div className="bg-white border border-orange-200 rounded-lg p-4">
                    <h4 className="font-bold text-gray-900 mb-2">3. Impacto em contratos de longo prazo</h4>
                    <p className="text-gray-700 text-sm">
                      Contratos firmados antes da Reforma podem ficar desbalanceados. É essencial revisar cláusulas de reajuste e repasse de tributos.
                    </p>
                  </div>

                  <div className="bg-white border border-orange-200 rounded-lg p-4">
                    <h4 className="font-bold text-gray-900 mb-2">4. Novo papel do cadastro de produtos/serviços</h4>
                    <p className="text-gray-700 text-sm">
                      Classificação fiscal (NCM, CEST, código de serviço) ganha importância crítica. Erros de enquadramento podem gerar autuações significativas.
                    </p>
                  </div>

                  <div className="bg-white border border-orange-200 rounded-lg p-4">
                    <h4 className="font-bold text-gray-900 mb-2">5. Cronograma de transição gradual (2026–2033)</h4>
                    <p className="text-gray-700 text-sm">
                      Durante 7 anos, haverá <strong>coexistência de tributos antigos e novos</strong>. Isso aumenta complexidade e exige planejamento cuidadoso para cada ano da transição.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-information-line text-blue-600"></i>
                  O que fazer agora:
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">→</span>
                    <span>Acompanhe as <strong>notas técnicas da Receita Federal</strong> sobre CBS/IBS</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">→</span>
                    <span>Atualize seu ERP para emitir documentos fiscais com novos campos obrigatórios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">→</span>
                    <span>Revise cadastro de produtos/serviços e classificação fiscal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">→</span>
                    <span>Simule impactos da Reforma na sua carga tributária atual</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">→</span>
                    <span>Treine equipe fiscal sobre as novas regras</span>
                  </li>
                </ul>
                <p className="text-gray-700 mt-3">
                  💡 <strong>Dica:</strong> Leia nosso artigo completo sobre <Link to="/artigo/cbs-ibs-nova-emissao-nota-fiscal-reforma-tributaria" className="text-blue-600 hover:underline">CBS e IBS: Nova Emissão de Nota Fiscal e Reforma Tributária</Link> para entender todos os detalhes.
                </p>
              </div>
            </section>

            {/* Seção 7 - Erros Comuns */}
            <section id="erros" className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Erros comuns que viram autuação (e como evitar)</h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                Mesmo com boas intenções, empresas cometem erros que podem resultar em autuações pesadas. Abaixo, os mais comuns e como evitá-los:
              </p>

              <div className="space-y-4">
                <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    ❌ Erro 1: Distribuir lucros sem lastro contábil
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    Distribuir lucros além do lucro contábil apurado ou sem escrituração correta pode ser interpretado como pró-labore disfarçado ou sonegação.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Como evitar:</strong> Mantenha contabilidade rigorosa, apure lucro real periodicamente e documente todas as distribuições.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    ❌ Erro 2: Escolher regime sem simulação (ou manter por inércia)
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    Permanecer no Simples quando o Presumido seria mais vantajoso (ou vice-versa) pode custar milhares de reais por ano.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Como evitar:</strong> Faça simulação tributária anual com cenários realistas. Reavalie sempre que houver mudanças significativas no negócio.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    ❌ Erro 3: Classificação fiscal errada (CNAE, NCM, código de serviço)
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    Enquadramento incorreto pode gerar alíquota errada, obrigações acessórias inadequadas e autuação.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Como evitar:</strong> Revise periodicamente a classificação com contador especializado. Consulte tabelas oficiais da Receita Federal e IBGE.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    ❌ Erro 4: Aproveitar créditos indevidos ou sem documentação
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    Creditar PIS/COFINS sobre despesas não permitidas ou sem nota fiscal idônea é um dos principais motivos de autuação.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Como evitar:</strong> Estude a legislação sobre créditos permitidos, mantenha documentação completa e faça revisão mensal por amostragem.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    ❌ Erro 5: Criar empresas de fachada ou operações fictícias
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    Estruturas sem substância econômica real (criadas apenas para reduzir tributos) configuram evasão e podem resultar em processo criminal.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Como evitar:</strong> Toda estrutura tributária deve ter <strong>substância econômica real</strong>: operações efetivas, colaboradores, contratos genuínos e finalidade comercial clara.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    ❌ Erro 6: Falta de documentação do planejamento tributário
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    Mesmo decisões corretas podem ser questionadas se não houver registros, justificativas e memória de cálculo.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Como evitar:</strong> Documente tudo: premissas, simulações, decisões, aprovações e revisões periódicas.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    ❌ Erro 7: Ignorar mudanças na legislação
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    Leis tributárias mudam com frequência. Continuar aplicando regras antigas pode gerar inconsistências e multas.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Como evitar:</strong> Acompanhe publicações oficiais (Diário Oficial, Receita Federal, SEFAZ), participe de cursos de atualização e consulte contador especializado.
                  </p>
                </div>
              </div>
            </section>

            {/* Seção 8 - FAQ */}
            <section id="faq" className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Perguntas Frequentes (FAQ)</h2>
              
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-teal-600 mt-0.5"></i>
                    <span>Planejamento tributário é legal?</span>
                  </h3>
                  <p className="text-gray-700 text-sm ml-7">
                    Sim, desde que feito dentro da lei (elisão fiscal). É um direito do contribuinte organizar suas operações da forma mais eficiente possível. O que é ilegal é a evasão (sonegação, fraude, omissões).
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-teal-600 mt-0.5"></i>
                    <span>Qual é o melhor regime tributário para minha empresa?</span>
                  </h3>
                  <p className="text-gray-700 text-sm ml-7">
                    Depende de múltiplos fatores: faturamento, margem de lucro, atividade, folha de pagamento, tipo de cliente, despesas dedutíveis. A melhor forma de descobrir é fazer uma <strong>simulação comparativa</strong> entre Simples, Presumido e Real com dados reais da sua empresa.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-teal-600 mt-0.5"></i>
                    <span>Posso mudar de regime tributário durante o ano?</span>
                  </h3>
                  <p className="text-gray-700 text-sm ml-7">
                    Não. A mudança de regime geralmente só pode ser feita <strong>no início do ano-calendário</strong> (com opção exercida até o final de janeiro). Exceções ocorrem em casos de abertura de empresa ou mudanças estruturais significativas.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-teal-600 mt-0.5"></i>
                    <span>Distribuição de lucros é sempre isenta de imposto?</span>
                  </h3>
                  <p className="text-gray-700 text-sm ml-7">
                    Sim, <strong>desde que haja lucro contábil apurado e escrituração regular</strong>. Distribuir valores além do lucro real ou sem contabilidade adequada pode ser questionada como distribuição disfarçada de pró-labore.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-teal-600 mt-0.5"></i>
                    <span>Como a Reforma Tributária (CBS/IBS) afeta meu planejamento?</span>
                  </h3>
                  <p className="text-gray-700 text-sm ml-7">
                    A partir de 2026, começam os testes de CBS e IBS, que substituirão PIS/COFINS, ICMS e ISS gradualmente. Isso muda completamente a sistemática de cálculo, créditos, precificação e documentação fiscal. É essencial começar a se preparar agora.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-teal-600 mt-0.5"></i>
                    <span>Vale a pena contratar consultoria tributária?</span>
                  </h3>
                  <p className="text-gray-700 text-sm ml-7">
                    Depende do tamanho e complexidade da empresa. Para negócios com faturamento acima de R$ 500 mil/ano, geralmente <strong>a economia gerada por um bom planejamento supera o custo da consultoria</strong>. Além disso, reduz riscos de autuação e multas.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-teal-600 mt-0.5"></i>
                    <span>O que acontece se eu for autuado por erro no planejamento tributário?</span>
                  </h3>
                  <p className="text-gray-700 text-sm ml-7">
                    Você terá direito a <strong>defesa administrativa</strong> (impugnação e recursos). Se o erro foi de boa-fé e você tiver documentação que justifique suas escolhas, as chances de reverter ou reduzir a penalidade são maiores. Por isso a documentação é tão importante.
                  </p>
                </div>
              </div>
            </section>

            {/* Conclusão */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Conclusão: planejamento tributário como vantagem competitiva</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Planejamento tributário bem feito não é "jeitinho" ou "gambiarra" — é <strong>gestão profissional</strong>. Empresas que investem tempo e recursos para estruturar operações, escolher regimes adequados, aproveitar créditos legais e documentar decisões ganham vantagem competitiva significativa.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Com a <strong>Reforma Tributária</strong> já em curso e mudanças profundas chegando a partir de 2026, o papel do planejamento se torna ainda mais crítico. Quem se antecipa, adaptar processos e treinar equipes terá menos riscos e mais oportunidades.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Lembre-se:</strong> planejamento tributário não é algo que se faz "uma vez por ano". É um processo contínuo, que exige revisão periódica, acompanhamento de mudanças legais e governança rigorosa. Invista nisso — o retorno é certo.
              </p>
            </section>

            {/* CTA Final */}
            <div className="mt-16 p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Precisa de ajuda com Planejamento Tributário?
              </h3>
              <p className="text-gray-700 mb-6">
                Nossa equipe de especialistas pode ajudar sua empresa a escolher o regime tributário ideal, estruturar operações de forma eficiente e reduzir a carga tributária dentro da lei. Entre em contato e descubra quanto sua empresa pode economizar.
              </p>
              <Link 
                to="/contato"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Solicite uma análise tributária gratuita
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </article>
      </div>
      
      <Footer />
    </>
  );
}
